// The weekly species report, from a pivot of [week, collection site] rows by
// species columns, laid out as the customers' template has it:
//
//   Week start | Collection site | Total count | Weekly change | species... |
//   Bycatch | Unknown species
//
// Weekly change is this week's total for the site minus the previous week's.
// A site with no specimens in either week counts 0 for it: a site that drops
// out still gets a row for the week it dropped out, with its fall. The first
// week of a date-filtered report has no previous week loaded, so its change is
// left blank rather than guessed.
import { TOTAL_LABEL, cellValue } from "./pivotModel";

export const ALL_WEEKS = "all";

const addDays = (iso, days) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

const siteOrder = (a, b) => {
  // No collection site last, the rest by name ignoring case
  const none = (e) => (e.category[1] === "none" ? 1 : 0);
  return none(a) - none(b) || a.label[1].localeCompare(b.label[1], undefined, { sensitivity: "base" });
};

// The template has Bycatch before Unknown species; everything else keeps the
// server's order.
const templateColumns = (columns) => {
  const order = (c) => ({ bycatch: 1, unknown: 2 })[c.category[0]] ?? 0;
  return columns
    .map((c, i) => ({ c, i }))
    .sort((a, b) => order(a.c) - order(b.c) || a.i - b.i);
};

const signed = (v) => (v > 0 ? `+${v.toLocaleString()}` : v < 0 ? `−${Math.abs(v).toLocaleString()}` : "0");

/** The weeks in the report, oldest first. */
export const weeksOf = (pivot) =>
  Array.from(new Set(pivot.rows.map((r) => r.key[0]))).sort();

/**
 * @param pivot      the pivot model (rows [week, collectionSite], columns [species])
 * @param week       a week start (YYYY-MM-DD) to show, or ALL_WEEKS
 * @param rangeStart the first day the report's date filter asks for, if any
 */
export const weeklyReport = (pivot, { week = ALL_WEEKS, rangeStart = null } = {}) => {
  const columns = templateColumns(pivot.columns);
  const totalOf = new Map(pivot.rows.map((row, r) => [`${row.key[0]}|${row.key[1]}`, pivot.rowTotals[r] ?? 0]));
  const weeks = weeksOf(pivot);
  const lastWeek = weeks[weeks.length - 1];
  const firstLoaded = rangeStart ?? null;

  // every row the server sent, plus a zero row for a site the week after it
  // last appeared, so its drop shows
  const rows = pivot.rows.map((row, r) => ({ entry: row, source: r }));
  pivot.rows.forEach((row) => {
    const next = addDays(row.key[0], 7);
    if (next > lastWeek || totalOf.has(`${next}|${row.key[1]}`)) return;
    totalOf.set(`${next}|${row.key[1]}`, 0);
    rows.push({
      entry: { key: [next, row.key[1]], label: [next, row.label[1]], category: [row.category[0], row.category[1]] },
      source: null,
    });
  });
  rows.sort((a, b) => a.entry.key[0].localeCompare(b.entry.key[0]) || siteOrder(a.entry, b.entry));

  const shown = week === ALL_WEEKS ? rows : rows.filter((r) => r.entry.key[0] === week);
  const values = new Map();
  shown.forEach((row, r) => {
    if (row.source == null) return;
    columns.forEach(({ i }, c) => {
      const v = cellValue(pivot, row.source, i);
      if (v) values.set(`${r}:${c}`, v);
    });
  });
  const totals = shown.map((row) => totalOf.get(`${row.entry.key[0]}|${row.entry.key[1]}`) ?? 0);
  const changes = shown.map((row) => {
    const [w, site] = row.entry.key;
    const previous = addDays(w, -7);
    if (firstLoaded && previous < firstLoaded) return null;
    return (totalOf.get(`${w}|${site}`) ?? 0) - (totalOf.get(`${previous}|${site}`) ?? 0);
  });
  const columnTotals = columns.map((_, c) =>
    shown.reduce((sum, __, r) => sum + (values.get(`${r}:${c}`) ?? 0), 0)
  );
  const grandTotal = totals.reduce((a, b) => a + b, 0);
  const knownChanges = changes.filter((v) => v != null);

  return {
    ...pivot,
    rows: shown.map((r) => r.entry),
    columns: columns.map(({ c }) => c),
    values,
    rowTotals: totals,
    columnTotals,
    grandTotal,
    leading: [
      { label: TOTAL_LABEL, values: totals, total: grandTotal },
      {
        label: "Weekly change",
        note:
          "This week's total count for the site minus the previous week's. A week with no specimens counts as 0. Blank where the report's dates leave out some of the previous week.",
        values: changes,
        format: signed,
        // a sum of changes only means something within one week
        total: week === ALL_WEEKS || knownChanges.length === 0 ? null : knownChanges.reduce((a, b) => a + b, 0),
      },
    ],
    leadingRows: [],
    totalColumn: false,
    totalRow: true,
  };
};
